import React, { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * EV-436（EV-430 の続き。PR #370 のダイアログと同じ作り）。必須の欄を `required` ではなく
 * `aria-required="true"` で伝え、空のまま送ったときだけアプリで止める。
 *
 * `required` の空欄は、WebKit が開いた直後（まだ何も入れていない）から「無効」として支援技術に伝え、
 * VoiceOver が「必須 無効なデータ」と読んでいた。送った後に空だった欄だけ `aria-invalid="true"` と
 * 知らせ（role=alert。欄と aria-describedby で結ぶ）を出し、最初の空欄へフォーカスを移す
 * （ブラウザの検証の吹き出しの代わり）。入れればその欄の知らせを消す。
 *
 * `fields` は「欄の名前 → 入力欄の id」。並びがフォーカスを移す順（画面の上から）。
 */
export interface RequiredFields<K extends string> {
  /** 空の欄があれば知らせを出して最初の空欄へフォーカスし、false を返す（送らない）。 */
  check: (values: Record<K, string | null | undefined>) => boolean;
  /** 入力が変わった欄の知らせを消す。 */
  clear: (name: K) => void;
  /**
   * 入力欄に付ける属性。`describedBy` はもとからの説明の id。`messageOf` は知らせを出す欄の名前
   * （確認コードの6つの枠のように、1つの知らせを複数の欄から指すとき）。
   */
  fieldProps: (
    name: K,
    options?: { describedBy?: string; messageOf?: K }
  ) => { 'aria-required': true; 'aria-invalid': true | undefined; 'aria-describedby': string | undefined };
  /** 空だったときの知らせ（role=alert）。`group` のどれかが空なら1つだけ出す。 */
  message: (name: K, options?: { group?: K[]; className?: string }) => React.ReactElement | null;
}

export function useRequiredFields<K extends string>(fields: Record<K, string>): RequiredFields<K> {
  const { t } = useTranslation('common');
  const baseId = useId();
  const [missing, setMissing] = useState<Partial<Record<K, true>>>({});
  const names = useMemo(() => Object.keys(fields) as K[], [fields]);
  const errorId = (name: K) => `${baseId}-${name}-required`;

  const check = (values: Record<K, string | null | undefined>) => {
    const next: Partial<Record<K, true>> = {};
    names.forEach((name) => {
      const value = values[name];
      if (value === null || value === undefined || value === '') next[name] = true;
    });
    setMissing(next);
    const first = names.find((name) => next[name]);
    if (first === undefined) return true;
    document.getElementById(fields[first])?.focus();
    return false;
  };

  const clear = (name: K) => {
    setMissing((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  };

  const fieldProps: RequiredFields<K>['fieldProps'] = (name, options = {}) => {
    const ids = [options.describedBy, missing[name] ? errorId(options.messageOf ?? name) : undefined]
      .filter(Boolean)
      .join(' ');
    return {
      'aria-required': true,
      'aria-invalid': missing[name] ? true : undefined,
      'aria-describedby': ids || undefined,
    };
  };

  const message: RequiredFields<K>['message'] = (name, options = {}) =>
    (options.group ?? [name]).some((n) => missing[n]) ? (
      <p id={errorId(name)} role="alert" className={options.className ?? 'mt-1 text-caption text-error'}>
        {t('ui.required-input', { ns: 'common' })}
      </p>
    ) : null;

  return { check, clear, fieldProps, message };
}
